import * as tus from 'tus-js-client';
import type { PickedFile } from './files.ts';

export interface UploadItem {
  id: string;
  name: string;
  relativePath: string;
  size: number;
  sent: number;
  state: 'queued' | 'uploading' | 'done' | 'error';
  error?: string;
}

export interface UploaderOptions {
  endpoint: string;
  chunkSize: number;
  sessionId?: string;
  token?: string;
  concurrency?: number;
  onChange: (items: UploadItem[]) => void;
}

/**
 * A small queue on top of tus. Concurrency is deliberately low: three parallel
 * streams saturate a hotel uplink about as well as ten, and a low number keeps
 * the byte counter honest.
 */
export class Uploader {
  private items: UploadItem[] = [];
  private queue: { item: UploadItem; picked: PickedFile }[] = [];
  private active = 0;
  private uploads = new Map<string, tus.Upload>();
  private options: UploaderOptions;

  constructor(options: UploaderOptions) {
    this.options = options;
  }

  add(files: PickedFile[]): void {
    for (const picked of files) {
      const item: UploadItem = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name: picked.file.name,
        relativePath: picked.relativePath,
        size: picked.file.size,
        sent: 0,
        state: 'queued',
      };
      this.items.push(item);
      this.queue.push({ item, picked });
    }
    this.emit();
    this.pump();
  }

  /**
   * Forget everything that has finished. Called once the flood has drained, so
   * the screen returns to the resting state instead of carrying a stale total
   * into the next drop. Anything still in flight is left alone.
   */
  clear(): void {
    this.items = this.items.filter((i) => i.state === 'uploading' || i.state === 'queued');
    this.emit();
  }

  abortAll(): void {
    for (const upload of this.uploads.values()) void upload.abort(true);
    this.uploads.clear();
    this.queue = [];
  }

  private emit(): void {
    this.options.onChange([...this.items]);
  }

  private pump(): void {
    const limit = this.options.concurrency ?? 3;
    while (this.active < limit && this.queue.length > 0) {
      const next = this.queue.shift();
      if (!next) break;
      this.start(next.item, next.picked);
    }
  }

  private start(item: UploadItem, picked: PickedFile): void {
    this.active += 1;
    item.state = 'uploading';
    this.emit();

    const metadata: Record<string, string> = {
      filename: picked.file.name,
      filetype: picked.file.type || 'application/octet-stream',
      relativePath: picked.relativePath,
    };
    if (this.options.sessionId) metadata.sessionId = this.options.sessionId;

    const headers: Record<string, string> = {};
    if (this.options.token) headers.Authorization = `Bearer ${this.options.token}`;

    const upload = new tus.Upload(picked.file, {
      endpoint: this.options.endpoint,
      chunkSize: this.options.chunkSize,
      metadata,
      headers,
      // a dropped connection in a hotel lobby is the normal case, not the edge
      retryDelays: [0, 1000, 3000, 6000, 12000, 24000],
      removeFingerprintOnSuccess: true,
      storeFingerprintForResuming: true,
      onProgress: (sent) => {
        item.sent = sent;
        this.emit();
      },
      onSuccess: () => {
        item.sent = item.size;
        item.state = 'done';
        this.uploads.delete(item.id);
        this.finish();
      },
      onError: (error) => {
        item.state = 'error';
        item.error = readableError(error);
        this.uploads.delete(item.id);
        this.finish();
      },
    });

    this.uploads.set(item.id, upload);
    upload.start();
  }

  private finish(): void {
    this.active -= 1;
    this.emit();
    this.pump();
  }
}

function readableError(error: Error | tus.DetailedError): string {
  const detailed = error as tus.DetailedError;
  const body = detailed.originalResponse?.getBody?.();
  if (body) {
    try {
      const parsed = JSON.parse(body) as { message?: string; error?: string };
      if (parsed.message) return parsed.message;
      if (parsed.error) return parsed.error;
    } catch {
      // not JSON; fall through
    }
  }
  return error.message || 'Upload failed';
}

export interface Totals {
  bytes: number;
  sent: number;
  files: number;
  done: number;
  failed: number;
  active: boolean;
}

export function totalsOf(items: UploadItem[]): Totals {
  let bytes = 0;
  let sent = 0;
  let done = 0;
  let failed = 0;
  for (const item of items) {
    bytes += item.size;
    sent += item.sent;
    if (item.state === 'done') done += 1;
    if (item.state === 'error') failed += 1;
  }
  return {
    bytes,
    sent,
    files: items.length,
    done,
    failed,
    active: items.some((i) => i.state === 'uploading' || i.state === 'queued'),
  };
}
